import { CaptchaAction } from './captcha'

type RenderParameters = {
  sitekey: string
  size: 'invisible'
  action: CaptchaAction
  callback: (token: string) => void
  'expired-callback': () => void
  'error-callback': () => void
}

type RecaptchaEnterprise = {
  ready: (callback: () => void) => void
  render: (container: HTMLElement, parameters: RenderParameters) => number
  execute: (widgetId: number) => void
  reset: (widgetId: number) => void
}

declare global {
  interface Window {
    grecaptcha?: { enterprise?: RecaptchaEnterprise }
  }
}

const SCRIPT_ID = 'recaptcha-enterprise'
const SCRIPT_LOAD_TIMEOUT_MS = 10_000
const EXECUTE_TIMEOUT_MS = 10_000

export const loadRecaptchaEnterprise = (
  siteKey: string,
  timeoutMs: number = SCRIPT_LOAD_TIMEOUT_MS
): Promise<RecaptchaEnterprise | undefined> =>
  new Promise((resolve) => {
    const timer = setTimeout(() => resolve(undefined), timeoutMs)

    const settle = (value: RecaptchaEnterprise | undefined) => {
      clearTimeout(timer)
      resolve(value)
    }

    // Timed too: `ready` only queues until Google's main bundle arrives, which can be blocked on its own.
    if (window.grecaptcha?.enterprise) {
      window.grecaptcha.enterprise.ready(() =>
        settle(window.grecaptcha?.enterprise)
      )
      return
    }

    const existing = document.getElementById(SCRIPT_ID)
    const script = existing ?? document.createElement('script')

    script.addEventListener('load', () => {
      window.grecaptcha?.enterprise?.ready(() =>
        settle(window.grecaptcha?.enterprise)
      )
    })
    script.addEventListener('error', () => {
      // A failed element never fires again, so leaving it would make every retry wait out the timeout.
      script.remove()
      settle(undefined)
    })

    if (!existing) {
      script.id = SCRIPT_ID
      script.setAttribute('async', '')
      script.setAttribute('defer', '')
      // recaptcha.net, not google.com: consent tools and CSPs can then allow reCAPTCHA by host
      // without allowing the rest of www.google.com. `explicit`, not the site key, or Google
      // auto-renders a second widget of its own.
      script.setAttribute(
        'src',
        'https://www.recaptcha.net/recaptcha/enterprise.js?render=explicit'
      )
      document.head.appendChild(script)
    }
  })

// By src, not title: Google translates the title into the browser's language.
const challengeFrames = () =>
  Array.from(
    document.querySelectorAll<HTMLIFrameElement>(
      'iframe[src*="/recaptcha/enterprise/bframe"]'
    )
  )

// The outermost wrapper, not the nearest styled ancestor: reCAPTCHA nests the iframe several
// divs deep and toggles `visibility` on the one it appends to the body.
const overlayOf = (frame: HTMLIFrameElement) => {
  let element = frame.parentElement

  while (element?.parentElement && element.parentElement !== document.body) {
    element = element.parentElement
  }

  return element ?? undefined
}

// Every widget gets its own overlay, and other widgets on the page (a second action, another
// library's widget) leave theirs behind hidden, so look at all of them.
const isChallengeOpen = () =>
  challengeFrames().some(
    (frame) => overlayOf(frame)?.style.visibility === 'visible'
  )

// A closed challenge fires no callback, so without this the promise never settles. Uses the same
// test as the execute deadline, so the two can never disagree about which overlay is ours.
const onChallengeDismissed = (onDismiss: () => void): (() => void) => {
  let wasOpen = isChallengeOpen()

  const observer = new MutationObserver(() => {
    const open = isChallengeOpen()

    if (wasOpen && !open) onDismiss()

    wasOpen = open
  })

  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['style'],
  })

  return () => observer.disconnect()
}

export type TokenMinter = {
  getToken: (action: CaptchaAction) => Promise<string | undefined>
  /** Loads the script and renders the action's widget without minting, so its badge shows before submit. */
  prepare: (action: CaptchaAction) => Promise<void>
  reset: () => void
  /** Removes the widgets and the element hosting them. */
  destroy: () => void
}

type Widget = { id: number; settle?: (token: string | undefined) => void }

export const createTokenMinter = (
  siteKey: string,
  container: HTMLElement,
  scriptLoadTimeoutMs?: number,
  executeTimeoutMs: number = EXECUTE_TIMEOUT_MS
): TokenMinter => {
  // One widget per action: the action is fixed when the widget renders, not passed to `execute`.
  const widgets = new Map<CaptchaAction, Widget>()

  const render = (
    enterprise: RecaptchaEnterprise,
    action: CaptchaAction
  ): Widget => {
    const host = document.createElement('div')
    container.appendChild(host)

    const widget: Widget = { id: 0 }

    // Created once, so they read the current caller's resolver rather than closing over the first.
    const finish = (token: string | undefined) => {
      const settle = widget.settle
      widget.settle = undefined
      settle?.(token)
    }

    widget.id = enterprise.render(host, {
      sitekey: siteKey,
      size: 'invisible',
      action,
      callback: finish,
      'expired-callback': () => finish(undefined),
      'error-callback': () => finish(undefined),
    })

    widgets.set(action, widget)

    return widget
  }

  const mint = async (action: CaptchaAction): Promise<string | undefined> => {
    const enterprise = await loadRecaptchaEnterprise(
      siteKey,
      scriptLoadTimeoutMs
    )

    if (!enterprise) return undefined

    const existing = widgets.get(action)
    const widget = existing ?? render(enterprise, action)

    if (existing) enterprise.reset(existing.id)

    return new Promise<string | undefined>((resolve) => {
      // Before `execute`, which settles synchronously when no challenge shows — after it leaks.
      const stopWatching = onChallengeDismissed(() =>
        widget.settle?.(undefined)
      )

      // `execute` can go quiet with no callback at all, e.g. when a consent blocker stops Google's
      // challenge frame. Give up then, but never on someone with a puzzle open.
      const deadline = setTimeout(() => {
        if (!isChallengeOpen()) widget.settle?.(undefined)
      }, executeTimeoutMs)

      widget.settle = (token) => {
        clearTimeout(deadline)
        stopWatching()
        resolve(token)
      }

      enterprise.execute(widget.id)
    })
  }

  // One resolver per widget, so a second caller would orphan the first. Share the token instead.
  const inFlight = new Map<CaptchaAction, Promise<string | undefined>>()

  return {
    getToken: (action) => {
      const pending = inFlight.get(action)

      // Presence, not truthiness: a pending token is a Promise, and testing one for truth reads as
      // a forgotten await.
      if (pending !== undefined) return pending

      const token = mint(action).finally(() => inFlight.delete(action))

      inFlight.set(action, token)

      return token
    },
    prepare: async (action) => {
      const enterprise = await loadRecaptchaEnterprise(
        siteKey,
        scriptLoadTimeoutMs
      )

      if (enterprise && !widgets.has(action)) render(enterprise, action)
    },
    reset: () => {
      const enterprise = window.grecaptcha?.enterprise

      if (!enterprise) return

      // Tokens are single use. Drop spent ones now, or the next challenge returns them.
      widgets.forEach((widget) => enterprise.reset(widget.id))
    },
    destroy: () => {
      widgets.clear()
      inFlight.clear()
      container.remove()
    },
  }
}
